import React, { useContext, useEffect } from "react";

import {
	FormProvider,
	useForm,
	useFormContext,
	useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DatePicker } from "@components-shared/mobile/inputs";
import { GamingHistoryRawFilterData } from "@data-types";
import { getUrlParamsAsObj } from "@state";
import { GamingHistoryStoreContext } from "@state/context/games";
import { betPeriods } from "@utils/common/BetPeriods";
import { DateTime } from "luxon";
import { observer } from "mobx-react";

const CasinoFilterForm = observer(function CasinoFilterForm() {
	const {
		disposeCurrentGamingHistoryState,
		gamingHistoryFilterStore: {
			setRawFilterData,
			categories,
			setDefaultFilterData,
		},
	} = useContext(GamingHistoryStoreContext);

	const methods = useForm();
	const { t } = useTranslation("mybets");
	const { register, handleSubmit, reset } = methods;

	const onSubmit = (data: GamingHistoryRawFilterData) => {
		setRawFilterData({
			...data,
		});
	};

	useEffect(() => {
		if (location.search !== "") {
			const params = getUrlParamsAsObj();
			// @ts-expect-error
			reset(params);
		} else {
			disposeCurrentGamingHistoryState();
		}

		return setDefaultFilterData;
	}, []);

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(onSubmit)} className="form">
				<div className="card--primary__body">
					<div className="form__field">
						<label
							htmlFor="gamingHistoryCategorySelect"
							className="label"
						>
							{t<string>("GAMING_HISTORY.FILTER.SELECT_CATEGORY")}
						</label>
						<select
							id="gamingHistoryCategorySelect"
							className="input input--med input--select"
							{...register("casinoGameCategoryId")}
						>
							<option value={"all"}>
								{t<string>(
									"GAMING_HISTORY.FILTER.ALL_CATEGORIES"
								)}
							</option>
							{categories?.map((category) => (
								<option
									key={category.name}
									value={category.casinoGameCategoryId}
								>
									{category.name}
								</option>
							))}
						</select>
					</div>

					<div className="form__field">
						<label
							htmlFor="gamingHistoryPeriodSelect"
							className="label"
						>
							{t<string>("GAMING_HISTORY.FILTER.SELECT_PERIOD")}
						</label>
						<select
							id="gamingHistoryPeriodSelect"
							className="input input--med input--select"
							{...register("period")}
						>
							{betPeriods.map((betPeriod) => (
								<option
									key={betPeriod.value}
									value={betPeriod.value}
								>
									{t<string>(
										`GAMING_HISTORY.FILTER.PERIODS.${betPeriod.name}`
									)}
								</option>
							))}
						</select>
					</div>

					<CustomPeriodInput />

					<div className="input--check">
						<input
							id="checkWin"
							className="input--check__box"
							type="checkbox"
							{...register("onlyWin")}
						/>
						<label
							htmlFor="checkWin"
							className="input--check__label"
						>
							{t<string>(
								"GAMING_HISTORY.FILTER.DISPLAY_ONLY_WINNINGS"
							)}
						</label>
					</div>
				</div>

				<div className="card--primary__footer">
					<button
						className="btn btn--base btn--primary"
						type="submit"
					>
						{t<string>("GAMING_HISTORY.FILTER.SUBMIT_BUTTON")}
					</button>
				</div>
			</form>
		</FormProvider>
	);
});

const CustomPeriodInput = function CustomPeriodInput() {
	const { setValue, getValues } = useFormContext();
	const currentPeriod = useWatch({ name: "period" });
	const {
		gamingHistoryFilterStore: { rawData },
	} = useContext(GamingHistoryStoreContext);

	useEffect(() => {
		if (currentPeriod === "custom") {
			setValue("from", rawData.from);
			setValue("to", rawData.to);
		}
	}, [currentPeriod, rawData.from, rawData.to]);

	if (currentPeriod !== "custom") {
		return null;
	}

	return (
		<div className="u-mar--bottom--xsml">
			<DatePicker
				label="mybets:PAGE.FILTER_FORM.START_DATE"
				name="from"
				className="form__field"
				required
			/>

			<DatePicker
				label="mybets:PAGE.FILTER_FORM.END_DATE"
				name="to"
				className="form__field"
				validate={{
					isBiggerThanStartDate: (value: string) => {
						const formattedStartDate = DateTime.fromISO(
							getValues("from")
						)
							.startOf("day")
							.toISO();
						const formattedEndDate = DateTime.fromISO(value)
							.endOf("day")
							.toISO();

						if (!formattedEndDate || !formattedStartDate) {
							return "COMMON.ERROR_MESSAGES.END_DATE_LOWER";
						}

						return (
							formattedEndDate > formattedStartDate ||
							"COMMON.ERROR_MESSAGES.END_DATE_LOWER"
						);
					},
				}}
				required
			/>
		</div>
	);
};

export default CasinoFilterForm;
